import type { Vessel, Drafts, Corrections, TankReading, HydrostaticRow, Survey } from '../types';
import { validateDraft, validateDensity, validateNumber } from './security';

// ==================== DISPLACEMENT METHOD ====================
// Formulas verified against:
// - AR Brink & Associates (DRAFT SURVEY FORMULAE)
// - UK P&I Club (Carefully to Carry 2023, Chapter 16)
// - MySeaTime (Draft Survey Calculations)
// - Virtue Marine (Step-by-Step Guide)
// - Canadian Draught Survey Book (Chapter 3)
// - eBallot (Measurement of Bulk Cargoes by Draft Survey)

export interface DirectHydroValues {
  displacement: number;
  tpc: number;
  mtc: number;
  lcf: number;
  dmtc?: number; // delta MTC for 2nd correction (optional)
}

export function calculateDraftSurvey(
  drafts: Drafts,
  vessel: Vessel,
  directHydro?: DirectHydroValues
): { displacement: number; corrections: Corrections; details: string[]; warnings: string[] } {
  if (!drafts) {
    throw new Error('Draft readings are required. Please enter all 6 draft values.');
  }
  const details: string[] = [];
  const warnings: string[] = [];

  // Validate inputs
  const fwdP = validateDraft(drafts.fwd_port, 'Forward Port');
  const fwdS = validateDraft(drafts.fwd_stbd, 'Forward Starboard');
  const midP = validateDraft(drafts.mid_port, 'Midship Port');
  const midS = validateDraft(drafts.mid_stbd, 'Midship Starboard');
  const aftP = validateDraft(drafts.aft_port, 'Aft Port');
  const aftS = validateDraft(drafts.aft_stbd, 'Aft Starboard');
  const density = validateDensity(drafts.density);

  // Mean draft per station
  const fwdMean = (fwdP + fwdS) / 2;
  const midMean = (midP + midS) / 2;
  const aftMean = (aftP + aftS) / 2;

  details.push(`Forward Mean: ${fwdMean.toFixed(3)} m`);
  details.push(`Midship Mean: ${midMean.toFixed(3)} m`);
  details.push(`Aft Mean: ${aftMean.toFixed(3)} m`);

  // Mean of Means = Quarter Mean Draft (direct formula)
  // Source: AR Brink, Canadian Survey Book, Virtue Marine: (Fwd + 6*Mid + Aft) / 8
  const meanOfMeans = (fwdMean + 6 * midMean + aftMean) / 8;
  details.push(`Mean of Means (Quarter Mean Draft): ${meanOfMeans.toFixed(3)} m`);

  // Trim
  const trim = aftMean - fwdMean;
  details.push(`Trim: ${trim.toFixed(3)} m (${trim > 0 ? 'trimmed by stern' : trim < 0 ? 'trimmed by bow' : 'even keel'})`);

  const lbp = vessel.lbp || 0;
  if (lbp > 0 && Math.abs(trim) > lbp * 0.02) {
    warnings.push(`Trim exceeds 2% of LBP (${(lbp * 0.02).toFixed(2)}m) — verify readings carefully`);
  }
  if (lbp > 0 && Math.abs(trim) > lbp * 0.05) {
    warnings.push('CRITICAL: Trim exceeds 5% of LBP — survey may be unreliable');
  }

  // List
  const list = ((fwdP - fwdS) + (midP - midS) + (aftP - aftS)) / 3;
  details.push(`List: ${list.toFixed(3)} m (${list > 0 ? 'port side down' : list < 0 ? 'starboard side down' : 'no list'})`);

  if (Math.abs(list) > 0.3) {
    warnings.push('Significant list detected (>0.3m) — list correction applied automatically');
  }

  // Hydrostatic values at Mean of Means
  let hydroAtMoM: HydrostaticRow;
  const hydro = vessel.hydrostatic;

  if (directHydro) {
    hydroAtMoM = {
      draft: meanOfMeans,
      displacement: directHydro.displacement,
      tpc: directHydro.tpc,
      mtc: directHydro.mtc,
      lcf: directHydro.lcf,
    };
    details.push(`Direct Hydrostatic Values: Disp=${directHydro.displacement.toFixed(2)} MT, TPC=${directHydro.tpc.toFixed(2)}, MTC=${directHydro.mtc.toFixed(2)}, LCF=${directHydro.lcf.toFixed(2)}m`);
  } else {
    if (!hydro || hydro.length === 0) {
      throw new Error('No hydrostatic data. Upload a table in vessel settings or use Quick Survey with direct values.');
    }
    hydroAtMoM = interpolateHydrostatic(hydro, meanOfMeans);
    if (Number.isNaN(hydroAtMoM.displacement) || Number.isNaN(hydroAtMoM.tpc) || Number.isNaN(hydroAtMoM.mtc) || Number.isNaN(hydroAtMoM.lcf)) {
    throw new Error('Invalid hydrostatic interpolation result — check your table values or direct inputs');
  }
  details.push(`Hydrostatic at MoM: Disp=${hydroAtMoM.displacement.toFixed(2)} MT, TPC=${hydroAtMoM.tpc.toFixed(2)}, MTC=${hydroAtMoM.mtc.toFixed(2)}, LCF=${hydroAtMoM.lcf.toFixed(2)}m`);
  }

  // LCF CONVENTION WARNING
  // European/American convention: LCF positive = AFT of midship
  // Asian convention: LCF negative = AFT of midship (OPPOSITE!)
  // We assume European convention. If user has Asian tables, signs will be wrong.
  if (hydroAtMoM.lcf > 0) {
    details.push(`LCF convention check: +${hydroAtMoM.lcf.toFixed(2)}m = AFT of midship (European convention)`);
  } else if (hydroAtMoM.lcf < 0) {
    details.push(`LCF convention check: ${hydroAtMoM.lcf.toFixed(2)}m = FWD of midship (European convention)`);
  }

  // ========== FIRST TRIM CORRECTION ==========
  // Formula: (Trim[cm] × LCF[m] × TPC) / LBP[m]
  // Source: AR Brink, UK P&I, eBallot, Canadian Survey Book
  // Sign: NATURALLY determined by LCF sign × trim sign
  //   - Trim by stern (+), LCF aft (+) → positive → ADD
  //   - Trim by stern (+), LCF fwd (-) → negative → SUBTRACT
  //   - Trim by bow (-), LCF fwd (-) → positive → ADD (negative × negative)
  //   - Trim by bow (-), LCF aft (+) → negative → SUBTRACT
  let firstCorrM = 0;
  let firstCorrMT = 0;

  if (lbp > 0 && hydroAtMoM.lcf !== undefined && hydroAtMoM.tpc !== undefined) {
    // Draft correction in meters: (Trim × LCF) / LBP
    firstCorrM = (trim * hydroAtMoM.lcf) / lbp;
    // Weight correction in MT: (Trim[cm] × LCF × TPC) / LBP
    firstCorrMT = (trim * 100 * hydroAtMoM.lcf * hydroAtMoM.tpc) / lbp;

    details.push(`1st Trim Correction (draft): ${firstCorrM >= 0 ? '+' : ''}${firstCorrM.toFixed(4)} m  [(Trim×LCF)/LBP]`);
    details.push(`1st Trim Correction (weight): ${firstCorrMT >= 0 ? '+' : ''}${firstCorrMT.toFixed(2)} MT  [(Trim[cm]×LCF×TPC)/LBP]`);
    details.push(`  Sign: ${firstCorrMT >= 0 ? 'ADD (+)' : 'SUBTRACT (−)'} — LCF is ${Math.abs(hydroAtMoM.lcf).toFixed(2)}m ${hydroAtMoM.lcf > 0 ? 'aft' : 'fwd'} of midship, trim is ${trim > 0 ? 'by stern' : 'by bow'}`);
  } else if (lbp === 0) {
    warnings.push('LBP is zero — trim corrections skipped. Set vessel LBP.');
  }

  // ========== SECOND TRIM CORRECTION ==========
  // Formula: (Trim[m]² × 50 × δMTC) / LBP[m]
  // Source: AR Brink, UK P&I, Virtue Marine, eBallot, Canadian Survey Book
  // Where δMTC = MTC at (MoM + 50cm) − MTC at (MoM − 50cm)
  // ALWAYS POSITIVE — confirmed by all sources
  let secondCorrM = 0;
  let secondCorrMT = 0;

  if (lbp > 0 && hydroAtMoM.mtc !== undefined) {
    let dMdz = 0;
    if (directHydro && directHydro.dmtc !== undefined) {
      dMdz = directHydro.dmtc;
      details.push(`  δMTC (provided): ${dMdz.toFixed(2)}`);
    } else if (!directHydro && hydro && hydro.length > 0) {
      const mtcPlus = interpolateHydrostatic(hydro, meanOfMeans + 0.5).mtc;
      const mtcMinus = interpolateHydrostatic(hydro, meanOfMeans - 0.5).mtc;
      dMdz = Math.abs(mtcPlus - mtcMinus);
      details.push(`  δMTC = |MTC@${(meanOfMeans + 0.5).toFixed(2)}m − MTC@${(meanOfMeans - 0.5).toFixed(2)}m| = |${mtcPlus.toFixed(2)} − ${mtcMinus.toFixed(2)}| = ${dMdz.toFixed(2)}`);
    } else {
      warnings.push('Second trim correction skipped: provide δMTC in Quick Survey for accuracy');
    }

    if (dMdz > 0) {
      // Draft correction: (Trim² × 50 × dM/dZ) / LBP
      secondCorrM = (Math.pow(trim, 2) * 50 * dMdz) / lbp;
      // Weight correction: (Trim² × 50 × δMTC) / LBP
      secondCorrMT = (Math.pow(trim, 2) * 50 * dMdz) / lbp;

      // ALWAYS POSITIVE
      secondCorrM = Math.abs(secondCorrM);
      secondCorrMT = Math.abs(secondCorrMT);

      details.push(`2nd Trim Correction (draft): +${secondCorrM.toFixed(4)} m  [ALWAYS + — Trim² term]`);
      details.push(`2nd Trim Correction (weight): +${secondCorrMT.toFixed(2)} MT  [(Trim²×50×δMTC)/LBP]`);
    }
  }

  // ========== LIST / HEEL CORRECTION ==========
  // Formula: 6 × (TPCdeep − TPCshallow) × (Draftdeep − Draftshallow)
  // Source: AR Brink (DRAFT SURVEY FORMULAE)
  // ALWAYS ADDED — heel increases waterplane area
  let listCorrMT = 0;

  if (Math.abs(list) > 0.001) {
    const draftDeep = Math.max(midP, midS);
    const draftShallow = Math.min(midP, midS);
    let tpcDeep = hydroAtMoM.tpc;
    let tpcShallow = hydroAtMoM.tpc;

    if (!directHydro && hydro && hydro.length > 0) {
      tpcDeep = interpolateHydrostatic(hydro, draftDeep).tpc;
      tpcShallow = interpolateHydrostatic(hydro, draftShallow).tpc;
    } else if (directHydro) {
      warnings.push('List/Heel correction uses single TPC value (approximate). For full accuracy, upload hydrostatic table.');
    }

    listCorrMT = 6 * (tpcDeep - tpcShallow) * (draftDeep - draftShallow);
    listCorrMT = Math.abs(listCorrMT); // Always positive

    details.push(`List/Heel Correction: +${listCorrMT.toFixed(2)} MT  [6×(TPCdeep−TPCshallow)×(Draftdeep−Draftshallow)]`);
    details.push(`  Deep side: ${draftDeep.toFixed(3)}m (TPC=${tpcDeep.toFixed(2)}), Shallow side: ${draftShallow.toFixed(3)}m (TPC=${tpcShallow.toFixed(2)})`);
  } else {
    details.push(`List/Heel Correction: 0.00 MT (no significant list)`);
  }

  // Quarter Mean Draft = Mean of Means + 1st correction (m) + 2nd correction (m)
  const quarterMean = meanOfMeans + firstCorrM + secondCorrM;
  details.push(`Quarter Mean Draft: ${meanOfMeans.toFixed(3)} + ${firstCorrM >= 0 ? '+' : ''}${firstCorrM.toFixed(4)} + ${secondCorrM.toFixed(4)} = ${quarterMean.toFixed(3)} m`);

  // Final displacement at quarter mean
  const finalDisp = interpolateDisplacement(hydro, quarterMean);
  details.push(`Displacement at Quarter Mean: ${finalDisp.toFixed(2)} MT`);

  // Apply trim corrections to displacement
  const dispAfterTrim = finalDisp + firstCorrMT + secondCorrMT + listCorrMT;
  details.push(`Displacement after trim corrections: ${finalDisp.toFixed(2)} + ${firstCorrMT >= 0 ? '+' : ''}${firstCorrMT.toFixed(2)} + ${secondCorrMT.toFixed(2)} + ${listCorrMT.toFixed(2)} = ${dispAfterTrim.toFixed(2)} MT`);

  // Density correction
  let densityCorr = 1;
  if (density !== 1.025) {
    densityCorr = density / 1.025;
    details.push(`Density Correction: ${densityCorr.toFixed(6)} (ρ=${density} vs standard 1.025)`);
  }

  const correctedDisp = dispAfterTrim * densityCorr;
  details.push(`Density-Corrected Displacement: ${dispAfterTrim.toFixed(2)} × ${densityCorr.toFixed(6)} = ${correctedDisp.toFixed(2)} MT`);

  const corrections: Corrections = {
    trim,
    list,
    first_corr_m: firstCorrM,
    first_corr_mt: firstCorrMT,
    second_corr_m: secondCorrM,
    second_corr_mt: secondCorrMT,
    list_corr_mt: listCorrMT,
    quarter_mean: quarterMean,
  };

  return {
    displacement: Number(correctedDisp.toFixed(3)),
    corrections,
    details,
    warnings,
  };
}

function interpolateHydrostatic(table: HydrostaticRow[], draft: number): HydrostaticRow {
  const sorted = [...table].sort((a, b) => a.draft - b.draft);
  const idx = sorted.findIndex((r, i) => i < sorted.length - 1 && draft >= r.draft && draft <= sorted[i + 1].draft);

  if (idx === -1) {
    if (draft < sorted[0].draft) return sorted[0];
    return sorted[sorted.length - 1];
  }

  const low = sorted[idx];
  const high = sorted[idx + 1];
  const ratio = (draft - low.draft) / (high.draft - low.draft);

  return {
    draft,
    displacement: low.displacement + (high.displacement - low.displacement) * ratio,
    tpc: low.tpc + (high.tpc - low.tpc) * ratio,
    mtc: low.mtc + (high.mtc - low.mtc) * ratio,
    lcf: low.lcf + (high.lcf - low.lcf) * ratio,
  };
}

function interpolateDisplacement(table: HydrostaticRow[], draft: number): number {
  const sorted = [...table].sort((a, b) => a.draft - b.draft);
  const idx = sorted.findIndex((r, i) => i < sorted.length - 1 && draft >= r.draft && draft <= sorted[i + 1].draft);

  if (idx === -1) {
    if (draft < sorted[0].draft) return sorted[0].displacement;
    return sorted[sorted.length - 1].displacement;
  }

  const low = sorted[idx];
  const high = sorted[idx + 1];
  const ratio = (draft - low.draft) / (high.draft - low.draft);
  return low.displacement + (high.displacement - low.displacement) * ratio;
}

// ==================== ULLAGE / TANKER METHOD ====================

function getVCF(temp: number, density: number): number {
  validateNumber(temp, 'Temperature', -50, 100);
  validateNumber(density, 'Density', 0.5, 1.2);
  const deltaT = temp - 15;
  let coeff = 1 - 0.0008 * deltaT;
  if (density < 0.78) coeff = 1 - 0.0010 * deltaT;
  else if (density > 0.9) coeff = 1 - 0.0007 * deltaT;
  return Math.max(0.5, Math.min(1.5, coeff));
}

export function calculateTankWeight(
  readings: TankReading[],
  vessel: Vessel
): { totalWeight: number; totalGSV: number; details: string[]; warnings: string[]; updated: TankReading[] } {
  const details: string[] = [];
  const warnings: string[] = [];
  let totalWeight = 0;
  let totalGSV = 0;

  if (!vessel.tank_tables || vessel.tank_tables.length === 0) {
    throw new Error('No tank tables loaded for this vessel. Add vessel data first.');
  }

  const updated = readings.map((r) => {
    const tank = vessel.tank_tables.find((t) => t.tank_no === r.tank_no);
    if (!tank) {
      warnings.push(`Tank ${r.tank_no} not found in vessel tables — skipped`);
      return { ...r, gov: 0, gsv: 0, weight: 0 };
    }

    const ullage = validateNumber(r.ullage, `Tank ${r.tank_no} ullage`, 0, 50);
    const temp = validateNumber(r.temp, `Tank ${r.tank_no} temp`, -50, 100);
    const obsDensity = validateNumber(r.observed_density, `Tank ${r.tank_no} density`, 0.5, 2.0);

    const volume = interpolateUllage(tank.table, ullage);
    details.push(`Tank ${r.tank_no} (${tank.name}): Ullage ${ullage.toFixed(3)}m → GOV ${volume.toFixed(2)} m³`);

    const vcf = getVCF(temp, obsDensity);
    const gsv = volume * vcf;
    details.push(`  VCF@${temp}°C: ${vcf.toFixed(5)} → GSV: ${gsv.toFixed(2)} m³`);

    const weight = gsv * obsDensity;
    details.push(`  Weight in Air: ${weight.toFixed(3)} MT (ρ=${obsDensity})`);

    totalWeight += weight;
    totalGSV += gsv;

    return { ...r, ullage, temp, observed_density: obsDensity, gov: volume, gsv, weight };
  });

  details.push(`TOTAL GSV: ${totalGSV.toFixed(2)} m³`);
  details.push(`TOTAL WEIGHT: ${totalWeight.toFixed(3)} MT`);

  return { totalWeight: Number(totalWeight.toFixed(3)), totalGSV: Number(totalGSV.toFixed(2)), details, warnings, updated };
}

function interpolateUllage(table: { ullage: number; volume: number }[], ullage: number): number {
  const sorted = [...table].sort((a, b) => a.ullage - b.ullage);
  const idx = sorted.findIndex((r, i) => i < sorted.length - 1 && ullage >= r.ullage && ullage <= sorted[i + 1].ullage);

  if (idx === -1) {
    if (ullage < sorted[0].ullage) return sorted[0].volume;
    return sorted[sorted.length - 1].volume;
  }

  const low = sorted[idx];
  const high = sorted[idx + 1];
  const ratio = (ullage - low.ullage) / (high.ullage - low.ullage);
  return low.volume + (high.volume - low.volume) * ratio;
}

// ==================== LPG METHOD ====================

export function calculateLPGWeight(
  readings: TankReading[],
  vessel: Vessel
): { totalWeight: number; details: string[]; warnings: string[]; updated: TankReading[] } {
  const details: string[] = [];
  const warnings: string[] = [];
  let totalWeight = 0;

  if (!vessel.tank_tables || vessel.tank_tables.length === 0) {
    throw new Error('No tank tables loaded for this vessel. Add vessel data first.');
  }

  const updated = readings.map((r) => {
    const tank = vessel.tank_tables.find((t) => t.tank_no === r.tank_no);
    if (!tank) {
      warnings.push(`Tank ${r.tank_no} not found — skipped`);
      return { ...r, weight: 0 };
    }

    const ullage = validateNumber(r.ullage, `Tank ${r.tank_no} ullage`, 0, 50);
    const temp = validateNumber(r.temp, `Tank ${r.tank_no} temp`, -60, 50);
    const density = validateNumber(r.observed_density, `Tank ${r.tank_no} density`, 0.4, 0.7);

    const volume = interpolateUllage(tank.table, ullage);
    const weight = volume * density;

    details.push(`Tank ${r.tank_no} (${tank.name}): ${volume.toFixed(2)} m³ × ${density} = ${weight.toFixed(3)} MT @ ${temp}°C`);
    totalWeight += weight;

    return { ...r, ullage, temp, observed_density: density, weight };
  });

  details.push(`TOTAL LPG WEIGHT: ${totalWeight.toFixed(3)} MT`);
  return { totalWeight: Number(totalWeight.toFixed(3)), details, warnings, updated };
}

// ==================== CARGO CALCULATION ====================

export function calculateCargo(
  initial: Survey,
  final: Survey,
  vessel: Vessel
): { cargoWeight: number; details: string[]; warnings: string[] } {
  const details: string[] = [];
  const warnings: string[] = [];

  let initialWeight = 0;
  let finalWeight = 0;

  if (initial.method === 'displacement') {
    initialWeight = initial.displacement || 0;
    details.push(`Initial Displacement: ${initialWeight.toFixed(2)} MT`);
  } else if (initial.method === 'ullage' || initial.method === 'lpg') {
    const tw = initial.tank_readings?.reduce((s, r) => s + (r.weight || 0), 0) || 0;
    initialWeight = tw;
    details.push(`Initial Tank Weight: ${initialWeight.toFixed(3)} MT`);
  }

  if (final.method === 'displacement') {
    finalWeight = final.displacement || 0;
    details.push(`Final Displacement: ${finalWeight.toFixed(2)} MT`);
  } else if (final.method === 'ullage' || final.method === 'lpg') {
    const tw = final.tank_readings?.reduce((s, r) => s + (r.weight || 0), 0) || 0;
    finalWeight = tw;
    details.push(`Final Tank Weight: ${finalWeight.toFixed(3)} MT`);
  }

  const initConst = Object.values(initial.constants || {}).reduce((a, b) => a + (b || 0), 0);
  const finalConst = Object.values(final.constants || {}).reduce((a, b) => a + (b || 0), 0);
  const constDiff = finalConst - initConst;

  details.push(`Initial Constants: ${initConst.toFixed(2)} MT`);
  details.push(`Final Constants: ${finalConst.toFixed(2)} MT`);
  details.push(`Constants Change: ${constDiff.toFixed(2)} MT`);

  const cargoWeight = (initialWeight - finalWeight) - constDiff;
  details.push(`CARGO WEIGHT: ${cargoWeight.toFixed(3)} MT`);

  if (cargoWeight < -1000) {
    warnings.push('Large negative cargo weight — verify this is a loading operation (cargo should be positive when loading)');
  }
  if (Math.abs(cargoWeight) > (vessel.light_ship || 0) * 2) {
    warnings.push('Cargo weight seems unrealistic compared to vessel light ship — double-check inputs');
  }

  return { cargoWeight: Number(cargoWeight.toFixed(3)), details, warnings };
}
